import type { EmissionReportImportFilters } from '../../services/emissionReportImportsApi';

/**
 * URL-Suchparameter <-> Filterzustand der Importliste (D-17). Reine Funktionen ohne React-,
 * Router- oder Mantine-Bezug, damit sie unter `environment: 'node'` automatisiert pruefbar sind
 * -- Vorbild: `pages/reports/reportsFilterUrl.ts`.
 *
 * Die Filter wirken ausschliesslich SERVERSEITIG: `toQueryFilters` liefert die Argumente fuer
 * `emissionReportImportsApi.getAll`, das daraus `status` und den Zeitraum auf
 * `measurementRangeStart` baut. Es wird niemals eine geladene Seite im Browser nachgefiltert --
 * auf einer abgeschnittenen Seite waere das schlicht falsch.
 */

/** Anzahl der Jahrgaenge im Jahresfilter. */
const YEAR_OPTION_COUNT = 5;

/**
 * `null` steht fuer „Alle Staende".
 *
 * Bewusst NUR die beiden Staende, die das Auswahlfeld anbietet -- `unreadable` ist serverseitig
 * ein gueltiger Wert, aber kein Filterangebot: ein „nicht lesbar"-Eintrag ist ein Einzelfall,
 * den man in der Gesamtliste sieht, kein Arbeitsvorrat, den man sich getrennt vornimmt. Ein
 * fremder Wert in der URL faellt deshalb auf „Alle Staende" zurueck, statt das Auswahlfeld mit
 * einem Wert zu fuellen, den es gar nicht kennt.
 */
export type EmissionReportStatusFilter = 'open' | 'accepted' | null;

export interface EmissionReportsFilterState {
  /** 1-basiert wie der `page`-Parameter von API Platform. */
  page: number;
  status: EmissionReportStatusFilter;
  year: number;
}

/** Wert des Auswahlfelds fuer „Alle Staende" -- Mantine `Select` vertraegt keinen leeren Wert. */
export const ALL_STATUSES = 'all';

export const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: ALL_STATUSES, label: 'Alle Stände' },
  { value: 'open', label: 'Nur zuzuordnen' },
  { value: 'accepted', label: 'Nur eingelesen' },
];

export function currentImportYear(): number {
  return new Date().getFullYear();
}

/** Die letzten fuenf Jahre, neuestes zuerst. */
export function yearFilterOptions(currentYear: number = currentImportYear()): number[] {
  return Array.from({ length: YEAR_OPTION_COUNT }, (_, offset) => currentYear - offset);
}

/** Uebersetzt den Rohwert des Auswahlfelds bzw. der URL in den Filterzustand. */
export function parseStatusValue(raw: string | null): EmissionReportStatusFilter {
  if (raw === 'open' || raw === 'accepted') {
    return raw;
  }
  return null;
}

/**
 * Liest die drei URL-Parameter in den Filterzustand. Ein ungueltiger Wert faellt still auf die
 * Vorgabe zurueck und wirft nie -- eine kaputte URL aus einem Lesezeichen soll die Seite zeigen,
 * nicht eine Fehlerseite.
 *
 * Vorgaben: Seite 1, „Alle Staende", laufendes Kalenderjahr.
 */
export function parseFilterParams(
  searchParams: URLSearchParams,
  currentYear: number = currentImportYear()
): EmissionReportsFilterState {
  const rawPage = searchParams.get('page');
  const parsedPage = rawPage === null ? NaN : Number(rawPage);
  const page = Number.isInteger(parsedPage) && parsedPage >= 1 ? parsedPage : 1;

  const rawYear = searchParams.get('year');
  const parsedYear = rawYear === null ? NaN : Number(rawYear);
  const year = yearFilterOptions(currentYear).includes(parsedYear) ? parsedYear : currentYear;

  return { page, status: parseStatusValue(searchParams.get('status')), year };
}

/**
 * Kehrt `parseFilterParams` um. Gibt die Parameter zurueck, statt sie selbst zu setzen -- nur so
 * ist die Funktion ohne Router pruefbar, der Aufrufer uebergibt das Ergebnis an
 * `setSearchParams`.
 *
 * Seite 1 und „Alle Staende" stehen nicht in der URL: sie sind die Vorgabe, und eine URL ohne
 * Rauschen laesst sich weitergeben.
 */
export function filterStateToSearchParams(state: EmissionReportsFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.page > 1) {
    params.set('page', String(state.page));
  }
  if (state.status !== null) {
    params.set('status', state.status);
  }
  params.set('year', String(state.year));
  return params;
}

/**
 * Die Abfrageparameter fuer `emissionReportImportsApi.getAll`. Das Jahr wandert als `year`
 * hinein und wird DORT zum Zeitraum auf `measurementRangeStart` -- die Umrechnung hat genau eine
 * Stelle im Projekt (`emissionReportImportsApi.buildQuery`), damit Seite und Client nicht
 * auseinanderlaufen koennen.
 */
export function toQueryFilters(state: EmissionReportsFilterState): EmissionReportImportFilters {
  const filters: EmissionReportImportFilters = { page: state.page, year: state.year };
  if (state.status !== null) {
    filters.status = state.status;
  }
  return filters;
}

/**
 * Seitenzahl aus der SERVERSEITIGEN letzten Seite (`PagedCollection.lastPage`, Punkt 11/IN-07).
 * Ein vom Server geliefertes Aggregat wird angezeigt, nicht aus einer kopierten Seitengroesse
 * nachgerechnet -- die frueher hier gefuehrte Konstante (Kopie des API-Platform-Standards) ist
 * deshalb entfallen. Ohne geladene Collection (`null`, noch nicht geladen) gilt Seite 1.
 */
export function pageCount(collection: { lastPage: number } | null): number {
  if (collection === null || !Number.isFinite(collection.lastPage) || collection.lastPage < 1) {
    return 1;
  }
  return collection.lastPage;
}
